/**
 * Autor: CI Sistemas de Informática Ltda. — AcessoiD
 * Onda 168 (16/10/2026): a apresentação comercial sai com o nome do cliente e o mês/ano na capa.
 *
 * O PDF guardado no servidor tem a linha do cliente EM BRANCO na página 1. No download (ou na
 * visualização), o navegador escreve ali "Cliente · outubro de 2026" com a Public Sans — a mesma
 * fonte da capa — e entrega o arquivo. Nada passa pelo servidor além do PDF base.
 *
 * A posição foi medida na impressão do PDF (deck_parking/imprimir_pdf.js → campo_cliente.json):
 * página de 1440 × 810 pt (1920 × 1080 px a 0,75 pt/px), origem embaixo à esquerda.
 * Se a capa mudar de lugar, medir de novo e trocar CAMPO_CAPA.
 *
 * Onda 169: o PDF base traz também as 3 páginas da proposta comercial (planos, o que está incluído,
 * condições) antes do contato. Sem a proposta, o navegador as retira; com ela, escreve "Preparada
 * para Cliente · 16 de outubro de 2026 · válida por 30 dias" na primeira delas.
 *
 * Onda 194: a página "Equipamentos" (1 servidor R$ 3.800 + 2 câmeras LPR R$ 4.860 = R$ 13.520, o estacionamento
 * convencional de uma entrada e uma saída) vem no PDF base logo depois dos planos; sai se não for pedida.
 * Valor mudou → regerar o PDF (deck_parking, slide proposta_equipamentos) e publicar.
 */
import fonteUrl from 'public-sans/binaries/webfonts/PublicSans-Regular.ttf';

export const CAMPO_CAPA = Object.freeze({
  x: 396.21,          // logo depois do selo "Apresentação comercial"
  baseline: 109.5,    // linha de base do texto, a partir da borda de baixo
  tamanho: 21,        // 28 px da capa
  tamanhoMinimo: 14,  // nome longo encolhe até aqui
  larguraMax: 947.79, // até a margem direita de 128 px
  cor: [201, 211, 217], // #C9D3D9
});

/**
 * Onde fica a proposta no PDF base (índice da página a partir de 0) e a linha "Preparada para…".
 * Onda 194: a proposta passou a 4 páginas — planos (13), EQUIPAMENTOS (14), o que está incluído e condições —
 * e o PDF base a 18. A de equipamentos (1 servidor + 2 câmeras LPR, o cenário de referência) só fica se for pedida.
 */
export const PROPOSTA = Object.freeze({
  inicio: 13, quantidade: 4, paginas: 18, equipamentos: 14,
  x: 96, baseline: 559.36, tamanho: 21, tamanhoMinimo: 14, larguraMax: 1248,
  cor: [74, 87, 99], // #4A5763
});

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto',
  'setembro', 'outubro', 'novembro', 'dezembro'];

/** "outubro de 2026" — o mês corrente pelo relógio local (Brasília), nunca por UTC. */
export function mesAnoPorExtenso(agora = new Date()) {
  return `${MESES[agora.getMonth()]} de ${agora.getFullYear()}`;
}

/** "16 de outubro de 2026" — a data de emissão da proposta. */
export function dataPorExtenso(agora = new Date()) {
  return `${agora.getDate()} de ${MESES[agora.getMonth()]} de ${agora.getFullYear()}`;
}

/** "Preparada para Cliente · 16 de outubro de 2026 · válida por 30 dias". */
export function textoDaProposta(cliente, agora = new Date()) {
  return `Preparada para ${limparCliente(cliente)} · ${dataPorExtenso(agora)} · válida por 30 dias`;
}

/** Espaços repetidos e nas pontas não entram na capa. */
export function limparCliente(nome) {
  return String(nome || '').replace(/\s+/g, ' ').trim();
}

/** O texto da capa: "Cliente · outubro de 2026". */
export function textoDaCapa(cliente, agora = new Date()) {
  return `${limparCliente(cliente)} · ${mesAnoPorExtenso(agora)}`;
}

/** Nome do arquivo com o cliente, sem acento nem caractere que o Windows recuse. */
export function nomeDoArquivo(base, cliente, agora = new Date(), { proposta = false } = {}) {
  const semExt = String(base || 'Apresentacao').replace(/\.pdf$/i, '');
  const slug = limparCliente(cliente).normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  return `${semExt}${proposta ? '_Proposta' : ''}${slug ? '_' + slug : ''}_${agora.getFullYear()}-${mes}.pdf`;
}

/**
 * A linha cabe numa linha só: encolhe até `tamanhoMinimo`; se ainda passar da margem, o nome é
 * cortado com reticências (o mês/ano nunca é cortado). `medir(texto, tamanho)` dá a largura em pt.
 */
export function ajustarLinha(cliente, agora, medir) {
  const mesAno = mesAnoPorExtenso(agora);
  let nome = limparCliente(cliente);
  let texto = `${nome} · ${mesAno}`;
  let tamanho = CAMPO_CAPA.tamanho;
  while (tamanho > CAMPO_CAPA.tamanhoMinimo && medir(texto, tamanho) > CAMPO_CAPA.larguraMax) tamanho -= 0.5;
  while (nome.length > 1 && medir(texto, tamanho) > CAMPO_CAPA.larguraMax) {
    nome = nome.slice(0, -1).trimEnd();
    texto = `${nome}… · ${mesAno}`;
  }
  return { texto, tamanho };
}

/**
 * Escreve a linha do cliente na página 1 e devolve os bytes do PDF novo.
 * `fonteBytes` existe para os testes; na tela, a fonte vem do próprio build.
 */
export async function personalizarPdf(pdfBytes, cliente, { agora = new Date(), fonteBytes, incluirProposta = false, incluirEquipamentos = false } = {}) {
  const [{ PDFDocument, rgb }, fontkitMod] = await Promise.all([import('pdf-lib'), import('@pdf-lib/fontkit')]);
  const fontkit = fontkitMod.default || fontkitMod;
  const doc = await PDFDocument.load(pdfBytes);
  doc.registerFontkit(fontkit);
  const bytesDaFonte = fonteBytes || await (await fetch(fonteUrl)).arrayBuffer();
  const fonte = await doc.embedFont(bytesDaFonte, { subset: true });

  const { texto, tamanho } = ajustarLinha(cliente, agora, (t, s) => fonte.widthOfTextAtSize(t, s));
  const [r, g, b] = CAMPO_CAPA.cor;
  doc.getPage(0).drawText(texto, {
    x: CAMPO_CAPA.x, y: CAMPO_CAPA.baseline, size: tamanho, font: fonte, color: rgb(r / 255, g / 255, b / 255),
  });
  // Onda 169: a proposta só é mexida se o PDF for o que tem as páginas dela no lugar esperado — um PDF
  // antigo (sem proposta) perderia páginas da apresentação se a retirada fosse feita às cegas.
  const temProposta = doc.getPageCount() === PROPOSTA.paginas;
  if (incluirProposta && !temProposta) throw new Error('Esta versão do PDF não tem a proposta comercial.');
  if (incluirProposta) {
    let tam = PROPOSTA.tamanho;
    const t = textoDaProposta(cliente, agora);
    while (tam > PROPOSTA.tamanhoMinimo && fonte.widthOfTextAtSize(t, tam) > PROPOSTA.larguraMax) tam -= 0.5;
    const [pr, pg, pb] = PROPOSTA.cor;
    doc.getPage(PROPOSTA.inicio).drawText(t, { x: PROPOSTA.x, y: PROPOSTA.baseline, size: tam, font: fonte, color: rgb(pr / 255, pg / 255, pb / 255) });
    // Onda 194: a página de equipamentos fica só quando pedida (ela só existe dentro da proposta).
    if (!incluirEquipamentos) doc.removePage(PROPOSTA.equipamentos);
  } else if (temProposta) {
    for (let i = PROPOSTA.inicio + PROPOSTA.quantidade - 1; i >= PROPOSTA.inicio; i--) doc.removePage(i);
  }
  doc.setTitle(`AcessoiD Parking — ${limparCliente(cliente)}`);
  return doc.save();
}
